(() => {
  const ORDER = ["year", "make", "model", "oem"];
  const datasetCache = new Map();
  const productCache = new Map();
  const FILTER_LABELS = {
    year: "Year",
    make: "Make",
    model: "Model",
    oem: "OEM",
  };

  const VISIBLE_LIMIT = 5;

  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const entryContainsYear = (entry, selectedYear) => {
    const compareValue = normalize(selectedYear);
    const years = Array.isArray(entry.year) ? entry.year : [entry.year];
    return years.some((yearItem) => {
      const normalized = normalize(yearItem);
      if (normalized.includes("-")) {
        const [start, end] = normalized.split("-").map((part) => parseInt(part, 10));
        const target = parseInt(compareValue, 10);
        return !Number.isNaN(start) && !Number.isNaN(end) && target >= start && target <= end;
      }
      return normalized === compareValue;
    });
  };

  const matchesEntry = (entry, key, value) => {
    if (!value) return true;
    if (key === "year") return entryContainsYear(entry, value);
    return normalize(entry[key]) === normalize(value);
  };

  const collectOptionsFromEntries = (entries, key) => {
    const set = new Set();
    entries.forEach((entry) => {
      if (key === "year") {
        const values = Array.isArray(entry.year) ? entry.year : [entry.year];
        values.forEach((value) => set.add(String(value)));
      } else if (entry[key]) {
        set.add(String(entry[key]));
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  };

  const filterByState = (entries, state, uptoIndex = ORDER.length - 1) => {
    return entries.filter((entry) => {
      return ORDER.slice(0, uptoIndex + 1).every((key) => matchesEntry(entry, key, state[key]));
    });
  };

  const createFilterState = () =>
    ORDER.reduce((acc, key) => {
      acc[key] = new Map();
      return acc;
    }, {});

  const escapeHtml = (text) =>
    String(text || "").replace(/[&<>"']/g, (char) => {
      const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
      return map[char];
    });

  const matchesEntryForSelections = (entry, key, selections) => {
    if (!selections || selections.size === 0) return true;
    if (key === "year") {
      return Array.from(selections.values()).some((value) => entryContainsYear(entry, value));
    }
    const candidate = normalize(entry[key]);
    return selections.has(candidate);
  };

  const filterDatasetWithSelections = (entries, state, excludeKey = null) => {
    return entries.filter((entry) => {
      return ORDER.every((key) => {
        if (excludeKey && key === excludeKey) return true;
        return matchesEntryForSelections(entry, key, state[key]);
      });
    });
  };

  const collectOptionsWithCounts = (entries, key) => {
    const counts = new Map();
    entries.forEach((entry) => {
      if (key === "year") {
        const values = Array.isArray(entry.year) ? entry.year : [entry.year];
        values.forEach((value) => {
          const label = String(value || "").trim();
          if (!label) return;
          counts.set(label, (counts.get(label) || 0) + 1);
        });
      } else if (entry[key]) {
        const label = String(entry[key]).trim();
        counts.set(label, (counts.get(label) || 0) + 1);
      }
    });
    return Array.from(counts.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { numeric: true })
    );
  };

  const parseParamValues = (value) => {
    if (!value) return [];
    return value
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part && part.toLowerCase() !== "null");
  };

  const createMessage = (text, className = "fitment-empty") =>
    `<p class="${className}">${text}</p>`;

  async function loadDataset(endpoint) {
    if (!datasetCache.has(endpoint)) {
      datasetCache.set(
        endpoint,
        fetch(endpoint, { cache: "no-cache" })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Fitment data not found (${response.status})`);
            }
            return response.json();
          })
          .catch((error) => {
            datasetCache.delete(endpoint);
            throw error;
          })
      );
    }

    return datasetCache.get(endpoint);
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const response = await fetch(`${window.location.origin}/products/${handle}.js`);
      if (!response.ok) throw new Error(`Product ${handle} not found`);
      const data = await response.json();
      productCache.set(handle, data);
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  class FitmentForm {
    constructor(section) {
      this.section = section;
      this.endpoint = section.dataset.fitmentEndpoint;
      this.controlsWrap = section.querySelector("[data-fitment-controls]");
      this.resultsWrap = section.querySelector("[data-fitment-results]");
      this.placeholderText = section.dataset.placeholderResults || "";
      this.submitButton = section.querySelector("[data-fitment-action='submit']");
      this.resultsUrl = section.dataset.resultsUrl || "/pages/fitment";
      this.selects = ORDER.reduce((acc, key) => {
        acc[key] = section.querySelector(`[data-fitment-select="${key}"]`);
        return acc;
      }, {});
      this.state = ORDER.reduce((acc, key) => ({ ...acc, [key]: null }), {});
      this.dataset = [];
      this.init();
    }

    async init() {
      try {
        this.dataset = await loadDataset(this.endpoint);
        this.populateSelect("year", collectOptionsFromEntries(this.dataset, "year"));
        this.bindEvents();
        this.renderPlaceholder();
      } catch (error) {
        console.error("Fitment form failed:", error);
        this.disableWidget("Unable to load fitment data. Please try again later.");
      }
    }

    bindEvents() {
      ORDER.forEach((key) => {
        const select = this.selects[key];
        select.addEventListener("change", () => {
          this.state[key] = select.value || null;
          this.resetAfter(key);
          const nextKey = ORDER[ORDER.indexOf(key) + 1];
          if (nextKey) {
            const filtered = filterByState(this.dataset, this.state, ORDER.indexOf(key));
            const options = collectOptionsFromEntries(filtered, nextKey);
            this.populateSelect(nextKey, options);
          }
          this.updateButtonState();
        });
      });

      this.submitButton.addEventListener("click", () => {
        if (this.submitButton.disabled) return;
        const url = new URL(this.resultsUrl, window.location.origin);
        const params = new URLSearchParams();
        ORDER.forEach((key) => params.set(key, this.state[key]));
        url.search = params.toString();
        window.location.href = url.toString();
      });
    }

    populateSelect(key, options) {
      const select = this.selects[key];
      const defaultOption = select.querySelector("option.default");
      select.innerHTML = "";
      if (defaultOption) {
        defaultOption.selected = true;
        defaultOption.disabled = true;
        defaultOption.value = "";
        select.appendChild(defaultOption);
      } else {
        const option = document.createElement("option");
        option.value = "";
        option.className = "default";
        option.textContent = select.dataset.placeholder || select.options[0]?.textContent || "";
        option.disabled = true;
        option.selected = true;
        select.appendChild(option);
      }

      options.forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });
      select.disabled = options.length === 0;
    }

    resetAfter(key) {
      const startIndex = ORDER.indexOf(key) + 1;
      ORDER.slice(startIndex).forEach((laterKey) => {
        this.state[laterKey] = null;
        const select = this.selects[laterKey];
        const placeholder = select.querySelector("option.default");
        select.innerHTML = "";
        if (placeholder) select.appendChild(placeholder);
        select.disabled = true;
      });
      this.renderPlaceholder();
    }

    updateButtonState() {
      const ready = ORDER.every((key) => Boolean(this.state[key]));
      this.submitButton.disabled = !ready;
    }

    renderPlaceholder() {
      if (!this.resultsWrap) return;
      this.resultsWrap.innerHTML = createMessage(this.placeholderText, "fitment-placeholder");
    }

    disableWidget(message) {
      this.section.querySelectorAll("select, button").forEach((el) => (el.disabled = true));
      if (this.resultsWrap) {
        this.resultsWrap.innerHTML = createMessage(message);
      }
    }
  }

class FitmentResults {
  constructor(section) {
    this.section = section;
    this.endpoint = section.dataset.fitmentEndpoint;
    this.resultsWrap = section.querySelector("[data-fitment-results]");
    this.filtersWrap = section.querySelector("[data-fitment-filter-groups]");
    this.filtersContainer = section.querySelector("[data-fitment-filters]"); // 新增：整个 aside 容器
    this.summaryEl = section.querySelector("[data-fitment-summary]");
    this.resultsCountEl = section.querySelector("[data-fitment-results-count]");
    this.activeFiltersWrap = section.querySelector("[data-fitment-active-filters]");
    this.clearFiltersBtn = section.querySelector("[data-fitment-clear]");
    this.mobileToggleBtn = section.querySelector("[data-fitment-toggle-mobile]"); // 新增：移动端按钮
    this.filtersBackdrop = null; // 新增：遮罩层引用
    this.placeholderText = section.dataset.placeholderResults || "";
    this.state = createFilterState();
    this.dataset = [];
    this.optionCounts = null;

    this.bindEvents();
    this.init();
  }

  bindEvents() {
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener("click", () => this.clearFilters());
    }

    if (this.filtersWrap) {
      // 原有：选项 change 事件
      this.filtersWrap.addEventListener("change", (event) => {
        const input = event.target;
        if (!input.matches("[data-fitment-filter-input]")) return;
        this.handleFilterChange(
          input.dataset.fitmentFilterInput,
          input.value,
          input.checked
        );
      });

      // 原有：折叠/展开 + Show more
      this.filtersWrap.addEventListener("click", (event) => {
        // 折叠/展开 group
        const headerBtn = event.target.closest("[data-fitment-filter-toggle]");
        if (headerBtn) {
          const key = headerBtn.dataset.fitmentFilterToggle;
          const group = this.filtersWrap.querySelector(
            `[data-fitment-filter="${key}"]`
          );
          if (group) {
            group.classList.toggle("is-collapsed");
          }
          return;
        }

        // Show more / Show less
        const showMoreBtn = event.target.closest("[data-fitment-show-more]");
        if (showMoreBtn) {
          const key = showMoreBtn.dataset.fitmentShowMore;
          const container = this.filtersWrap.querySelector(
            `[data-fitment-filter-options="${key}"]`
          );
          if (container) {
            const expanded = container.dataset.expanded === "true";
            container.dataset.expanded = expanded ? "false" : "true";

            if (this.optionCounts) {
              // 只刷新左侧 options
              this.renderFilterOptions(this.optionCounts);
            } else {
              this.refreshUI();
            }
          }
        }
      });
    }

    if (this.activeFiltersWrap) {
      this.activeFiltersWrap.addEventListener("click", (event) => {
        const button = event.target.closest("[data-fitment-chip-remove]");
        if (!button) return;
        this.removeSingleFilter(
          button.dataset.fitmentChipRemove,
          button.dataset.fitmentChipValue
        );
      });
    }

    // 新增：移动端抽屉开关 + 遮罩
    this.setupMobileFilters();
  }

  // === 新增：移动端相关 ===
  setupMobileFilters() {
    // 遮罩层 DOM
    if (this.filtersContainer) {
      const backdrop = document.createElement("div");
      backdrop.className = "fitment-filters-backdrop";
      this.section.appendChild(backdrop);
      this.filtersBackdrop = backdrop;

      backdrop.addEventListener("click", () => this.closeMobileFilters());
    }

    // 按钮点击
    if (this.mobileToggleBtn) {
      this.mobileToggleBtn.addEventListener("click", () => {
        this.toggleMobileFilters();
      });
    }
  }

  toggleMobileFilters(forceOpen) {
    const shouldOpen =
      typeof forceOpen === "boolean" ? forceOpen : !this.isMobileFiltersOpen();
    if (shouldOpen) {
      this.openMobileFilters();
    } else {
      this.closeMobileFilters();
    }
  }

  isMobileFiltersOpen() {
    return (
      this.filtersContainer &&
      this.filtersContainer.classList.contains("fitment-filters--open")
    );
  }

  openMobileFilters() {
    if (!this.filtersContainer) return;
    this.filtersContainer.classList.add("fitment-filters--open");
    if (this.filtersBackdrop) {
      this.filtersBackdrop.classList.add("is-active");
    }
  }

  closeMobileFilters() {
    if (!this.filtersContainer) return;
    this.filtersContainer.classList.remove("fitment-filters--open");
    if (this.filtersBackdrop) {
      this.filtersBackdrop.classList.remove("is-active");
    }
  }
  // === 移动端相关结束 ===

  async init() {
    const searchParams = new URLSearchParams(window.location.search);
    ORDER.forEach((key) => {
      const values = searchParams
        .getAll(key)
        .flatMap((value) => parseParamValues(value));
      values.forEach((value) => {
        if (!value) return;
        this.state[key].set(normalize(value), value);
      });
    });

    this.updateSummary();

    try {
      this.dataset = await loadDataset(this.endpoint);
      this.renderFilters();
      this.refreshUI();
    } catch (error) {
      console.error("Fitment results failed:", error);
      this.showMessage("Unable to load fitment data. Please try again later.");
    }
  }

  updateSummary() {
    if (!this.summaryEl) return;
    const summary = ORDER.map((key) => {
      const values = Array.from(this.state[key].values());
      if (!values.length) return "Any";
      return values.join(", ");
    }).join(" · ");
    this.summaryEl.textContent = summary;
  }

  renderFilters() {
    if (!this.filtersWrap) return;

    this.filtersWrap.innerHTML = ORDER.map(
      (key) => `
        <div class="fitment-filter-group" data-fitment-filter="${key}">
          <button
            class="fitment-filter-group__header"
            type="button"
            data-fitment-filter-toggle="${key}"
          >
            <h4>${FILTER_LABELS[key] || key}</h4>
            <span class="fitment-filter-group__icon" aria-hidden="true"></span>
          </button>
          <div class="fitment-filter-options" data-fitment-filter-options="${key}"></div>
        </div>
      `
    ).join("");
  }

  refreshUI() {
    if (!this.dataset.length) return;
    const { entries, optionCounts } = this.computeFilteredData();

    this.optionCounts = optionCounts;

    this.renderFilterOptions(optionCounts);
    this.renderActiveFilters();
    this.updateClearButtonState();
    this.updateSummary();
    this.renderResultsFromEntries(entries);
  }

  renderFilterOptions(optionCounts) {
    if (!this.filtersWrap) return;

    ORDER.forEach((key) => {
      const optionsContainer = this.filtersWrap.querySelector(
        `[data-fitment-filter-options="${key}"]`
      );
      if (!optionsContainer) return;

      const countsMap = optionCounts[key] || new Map();
      let options = Array.from(countsMap.entries());
      const selected = Array.from(this.state[key].values());

      // 把已选项也加进列表（即使 count 为 0）
      selected.forEach((value) => {
        if (!countsMap.has(value)) {
          options.push([value, 0]);
        }
      });

      options.sort((a, b) =>
        a[0].localeCompare(b[0], undefined, { numeric: true })
      );

      if (!options.length) {
        optionsContainer.innerHTML = `<p class="fitment-filter-empty">${
          FILTER_LABELS[key]
        } unavailable</p>`;
        return;
      }

      const isExpanded = optionsContainer.dataset.expanded === "true";

      const htmlOptions = options
        .map(([value, count], index) => {
          const normalized = normalize(value);
          const checked = this.state[key].has(normalized);

          const isExtra = index >= VISIBLE_LIMIT && !checked;
          const hiddenClass =
            isExtra && !isExpanded ? " fitment-filter-option--hidden" : "";
          const extraAttr =
            index >= VISIBLE_LIMIT ? ' data-fitment-option-extra="true"' : "";
          const disabled = !checked && count === 0;

          return `
            <label class="fitment-filter-option${hiddenClass}"${extraAttr}>
              <span>
                <input
                  type="checkbox"
                  value="${escapeHtml(value)}"
                  data-fitment-filter-input="${key}"
                  ${checked ? "checked" : ""}
                  ${disabled ? "disabled" : ""}
                >
                ${escapeHtml(value)}
              </span>
              <span class="fitment-filter-option__count">${count}</span>
            </label>
          `;
        })
        .join("");

      const hasMore = options.length > VISIBLE_LIMIT;

      optionsContainer.innerHTML = htmlOptions;

      if (hasMore) {
        optionsContainer.insertAdjacentHTML(
          "beforeend",
          `
          <button
            type="button"
            class="fitment-filter-show-more"
            data-fitment-show-more="${key}"
          >
            ${isExpanded ? "Show less" : "Show more"}
          </button>
        `
        );
      }
    });
  }

  renderActiveFilters() {
    if (!this.activeFiltersWrap) return;
    const chips = [];
    ORDER.forEach((key) => {
      this.state[key].forEach((value) => {
        chips.push(`
          <span class="fitment-chip">
            ${FILTER_LABELS[key] || key}: ${escapeHtml(value)}
            <button
              type="button"
              data-fitment-chip-remove="${key}"
              data-fitment-chip-value="${escapeHtml(value)}"
              aria-label="Remove ${FILTER_LABELS[key] || key} ${value}"
            >&times;</button>
          </span>
        `);
      });
    });

    if (!chips.length) {
      this.activeFiltersWrap.hidden = true;
      this.activeFiltersWrap.innerHTML = "";
    } else {
      this.activeFiltersWrap.hidden = false;
      this.activeFiltersWrap.innerHTML = chips.join("");
    }
  }

  updateClearButtonState() {
    if (!this.clearFiltersBtn) return;
    const hasFilters = ORDER.some((key) => this.state[key].size > 0);
    this.clearFiltersBtn.disabled = !hasFilters;
  }

  computeFilteredData() {
    const optionCounts = ORDER.reduce((acc, key) => {
      acc[key] = new Map();
      return acc;
    }, {});
    const entries = [];

    this.dataset.forEach((entry) => {
      const matchesFlags = ORDER.map((key) =>
        matchesEntryForSelections(entry, key, this.state[key])
      );
      const matchesAll = matchesFlags.every(Boolean);
      if (matchesAll) {
        entries.push(entry);
      }

      ORDER.forEach((key, index) => {
        const matchesOthers = matchesFlags.every(
          (flag, idx) => idx === index || flag
        );
        if (!matchesOthers) return;

        if (key === "year") {
          const values = Array.isArray(entry.year) ? entry.year : [entry.year];
          values.forEach((value) => {
            const label = String(value || "").trim();
            if (!label) return;
            optionCounts[key].set(
              label,
              (optionCounts[key].get(label) || 0) + 1
            );
          });
        } else if (entry[key]) {
          const label = String(entry[key]).trim();
          optionCounts[key].set(
            label,
            (optionCounts[key].get(label) || 0) + 1
          );
        }
      });
    });

    return { entries, optionCounts };
  }

  async renderResultsFromEntries(matches) {
    if (!this.resultsWrap) return;
    this.resultsWrap.innerHTML = "<p>Loading compatible products…</p>";

    if (matches.length === 0) {
      this.showMessage("No products were found for this selection.");
      this.updateResultsCount(0);
      return;
    }

    const productHandles = [...new Set(matches.flatMap((entry) => entry.products || []))];
    if (productHandles.length === 0) {
      this.showMessage("Products have not been attached to this fitment yet.");
      this.updateResultsCount(0);
      return;
    }

    try {
      const cards = await Promise.all(
        productHandles.map(async (handle) => {
          const product = await fetchProduct(handle);
          if (!product) return "";
          return buildProductCard(product);
        })
      );
      const html = cards.filter(Boolean).join("");
      const validCount = cards.filter(Boolean).length;
      this.resultsWrap.innerHTML = html || createMessage("No products available.");
      this.updateResultsCount(validCount);
    } catch (error) {
      console.error(error);
      this.showMessage("Unable to load product information right now.");
      this.updateResultsCount(0);
    }
  }

  updateResultsCount(count) {
    if (!this.resultsCountEl) return;
    if (count === 0) {
      this.resultsCountEl.textContent = "No products";
      return;
    }
    this.resultsCountEl.textContent = `${count} ${
      count === 1 ? "product" : "products"
    }`;
  }

  handleFilterChange(key, value, checked) {
    if (!this.state[key]) return;
    if (!value) return;
    if (checked) {
      this.state[key].set(normalize(value), value);
    } else {
      this.state[key].delete(normalize(value));
    }
    this.refreshUI();
  }

  removeSingleFilter(key, value) {
    if (!this.state[key] || !value) return;
    this.state[key].delete(normalize(value));
    this.refreshUI();
  }

  clearFilters() {
    ORDER.forEach((key) => this.state[key].clear());
    this.refreshUI();
  }

  showMessage(text) {
    if (this.resultsWrap) {
      const message = text || this.placeholderText || "";
      this.resultsWrap.innerHTML = createMessage(message);
    }
  }
}


  const buildProductCard = (product) => {
    const price = Shopify?.formatMoney
      ? Shopify.formatMoney(product.price)
      : `$${(product.price / 100).toFixed(2)}`;
    const image = product.images?.[0] || "";
    return `
      <article class="fitment-result-card">
        ${
          image
            ? `<a href="${product.url}" class="fitment-result-image"><img src="${image}" alt="${product.title}"></a>`
            : ""
        }
        <div class="fitment-result-info">
          <h4><a href="${product.url}">${product.title}</a></h4>

        </div>
        <div class="fitment-result-price">${price}</div>
      </article>
    `;
  };

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll('[data-fitment-mode="form"]').forEach((section) => {
      new FitmentForm(section);
    });

    document.querySelectorAll('[data-fitment-mode="results"]').forEach((section) => {
      new FitmentResults(section);
    });
  });
})();
